import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { change, Field, formValueSelector, initialize, reduxForm } from 'redux-form';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import { BasicModal, DefaultButton, QueryBuilder } from 'common/components';
import { FormMessageHOC } from 'common/HOCs';
import ObjectSchemaComposer from '../ObjectSchemaComposer';
import { runSubmitInterceptors } from '../submitInterceptors';

const regexForAccoutTypeEdit = /^\/admin\/PlatformsCategory\/PlatformAccountTypeResource\/\d+$/;
const regexForRewardTableEdit = /^\/admin\/PartnershipCategory\/RewardTableResource\/\d+$/;
const regexForMasterRewardTableEdit = /^\/admin\/PartnershipCategory\/MasterRewardTableResource\/\d+$/;

const fillEmptyField = (payload, query, value) => {
  const arrayRegex = /^([^[]+)\s*(\[\s*(\d+)\s*\]\s*)+\s*$/;
  const keys = query.split('.');
  const keyName = keys.pop();
  const nestedObject = keys.reduce((object, key) => {
    const match = key.match(arrayRegex);
    if (match) {
      const variableName = match[1];
      const index = match[3];
      if (!object[variableName]) {
        object[variableName] = [];
      }
      if (!object[variableName][index]) {
        object[variableName][index] = {};
      }
      return object[variableName][index];
    }
    if (!object[key]) {
      object[key] = {};
    }
    return object[key];
  }, payload);
  nestedObject[keyName] = value;
};

// 填充缺失的 boolean 字段默认值
const fillBooleanDefaults = (payload, dsl) => {
  if (!dsl || !dsl.properties || !payload) {
    return payload;
  }

  // 公共逻辑：递归填充所有 checkbox 字段的默认值
  const fillValue = (value, schema) => {
    if (!schema || !value || typeof value !== 'object') return;

    if (schema.properties) {
      Object.keys(schema.properties).forEach(key => {
        const fieldSchema = schema.properties[key];
        if (!fieldSchema) return;

        if (fieldSchema.widget === 'checkbox') {
          if (value[key] === undefined) {
            value[key] = fieldSchema.default !== undefined ? fieldSchema.default : false;
          }
        } else if (value[key] !== undefined) {
          fillValue(value[key], fieldSchema);
        } else if (
          fieldSchema.widget === 'object' ||
          fieldSchema.widget === 'dynamicform' ||
          fieldSchema.widget === 'array'
        ) {
          if (fieldSchema.widget === 'object' && value[key] === undefined) {
            value[key] = {};
            fillValue(value[key], fieldSchema);
          }
        }
      });
    }

    if ((schema.type === 'dynamic' || schema.widget === 'dynamicform') && schema.options) {
      if (value.type && value.value !== undefined) {
        const selectedOption = schema.options[value.type];
        if (selectedOption && selectedOption.form) {
          fillValue(value.value, selectedOption.form);
        }
      }
    }

    if (schema.widget === 'array' && schema.items && Array.isArray(value)) {
      value.forEach(item => {
        fillValue(item, schema.items);
      });
    }
  };

  fillValue(payload, dsl);

  // --- 特殊逻辑代码 ---
  // 处理 basic.program 的情况（OneOfField/DynamicForm）
  const programSchema = dsl.properties.basic?.properties?.program;
  // OneOfField 在前端的 DSL 中，type 是 "dynamic"，widget 是 "dynamicform"，options 是一个对象
  if (
    programSchema &&
    (programSchema.type === 'dynamic' || programSchema.widget === 'dynamicform') &&
    programSchema.options
  ) {
    const programValue = payload?.basic?.program;
    if (programValue && programValue.type) {
      // 找到对应的 option（options 是一个对象，key 是 type_slug）
      const selectedOptionForm = programSchema.options[programValue.type];
      if (selectedOptionForm && selectedOptionForm.form && selectedOptionForm.form.properties) {
        // 确保 basic.program.value 存在
        if (!payload.basic) payload.basic = {};
        if (!payload.basic.program) payload.basic.program = {};
        if (!payload.basic.program.value) payload.basic.program.value = {};

        // 遍历该 option 的 form 下的所有字段
        Object.keys(selectedOptionForm.form.properties).forEach(key => {
          const fieldSchema = selectedOptionForm.form.properties[key];

          // 如果是对象类型（如 margin_restriction）
          if (fieldSchema.widget === 'object' && fieldSchema.properties) {
            // 确保对象存在（即使已存在也要确保是对象）
            if (!payload.basic.program.value[key] || typeof payload.basic.program.value[key] !== 'object') {
              payload.basic.program.value[key] = {};
            }

            // 遍历对象内的字段
            Object.keys(fieldSchema.properties).forEach(nestedKey => {
              const nestedFieldSchema = fieldSchema.properties[nestedKey];

              // 如果是 boolean 字段且有默认值，但 payload 中不存在或为 undefined
              if (nestedFieldSchema.widget === 'checkbox' && nestedFieldSchema.default !== undefined) {
                if (payload.basic.program.value[key][nestedKey] === undefined) {
                  payload.basic.program.value[key][nestedKey] = nestedFieldSchema.default;
                }
              }
            });
          }
        });
      }
    }
  }

  // 处理 regular_account.group_exceptions 和 regular_account.symbol_exceptions 中的 is_set_* 字段
  if (payload.regular_account) {
    // 处理 group_exceptions
    if (Array.isArray(payload.regular_account.group_exceptions)) {
      payload.regular_account.group_exceptions.forEach(exception => {
        // 只处理 type 为 "fix" 的情况
        if (exception && exception.type === 'fix') {
          // 确保数据结构存在
          if (!exception.value) {
            exception.value = {};
          }
          if (!exception.value.value) {
            exception.value.value = {};
          }
          const conditionValue = exception.value.value;
          // 如果字段不存在或为 undefined，设置为 false
          if (conditionValue.is_set_lot === undefined) {
            conditionValue.is_set_lot = false;
          }
          if (conditionValue.is_set_gross_deposit === undefined) {
            conditionValue.is_set_gross_deposit = false;
          }
          if (conditionValue.is_set_net_deposit === undefined) {
            conditionValue.is_set_net_deposit = false;
          }
        }
      });
    }

    // 处理 symbol_exceptions
    if (Array.isArray(payload.regular_account.symbol_exceptions)) {
      payload.regular_account.symbol_exceptions.forEach(exception => {
        // 只处理 data.type 为 "fix" 的情况
        if (exception && exception.data && exception.data.type === 'fix') {
          // 确保数据结构存在
          if (!exception.data.value) {
            exception.data.value = {};
          }
          if (!exception.data.value.value) {
            exception.data.value.value = {};
          }
          const conditionValue = exception.data.value.value;
          // 如果字段不存在或为 undefined，设置为 false
          if (conditionValue.is_set_lot === undefined) {
            conditionValue.is_set_lot = false;
          }
          if (conditionValue.is_set_gross_deposit === undefined) {
            conditionValue.is_set_gross_deposit = false;
          }
          if (conditionValue.is_set_net_deposit === undefined) {
            conditionValue.is_set_net_deposit = false;
          }
        }
      });
    }
  }

  return payload;
};

const DynamicForm = ({
  dsl,
  submitting,
  userFilters,
  submitFunction,
  initQueryFilterOnUpdateAccountType,
  handleSubmit,
  dispatch,
  invalid,
  disabled,
  formError,
  submitFailed,
  location,
  pristine,
  initialValues,
  history,
  isTagForm,
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const toggleModal = () => setModalOpen(!isModalOpen);
  const [filters, setFilters] = useState(userFilters);

  // 检查是否是 RewardTable 编辑页面
  const isRewardTableEdit = regexForRewardTableEdit.test(window.location.pathname);
  const isMasterRewardTableEdit = regexForMasterRewardTableEdit.test(window.location.pathname);

  // 当 initialValues 加载完成时，确保表单正确初始化
  useEffect(() => {
    if ((isRewardTableEdit || isMasterRewardTableEdit) && initialValues && Object.keys(initialValues).length > 0) {
      // 使用 initialize 确保表单状态正确，pristine 会被重置为 true
      dispatch(initialize('commonDynamicForm', initialValues));
    }
  }, [isRewardTableEdit, isMasterRewardTableEdit, initialValues, dispatch]);

  // 对于 RewardTable 编辑页面，如果 pristine 未定义或为 true，默认禁用按钮
  const shouldDisableForRewardTable =
    (isRewardTableEdit || isMasterRewardTableEdit) && (pristine === undefined || pristine === true);

  useEffect(() => {
    if (submitFailed && !submitting) {
      setModalOpen(true);
    }
  }, [submitFailed, submitting]);

  useEffect(() => {
    if (window.location.pathname === '/admin/PlatformsCategory/PlatformAccountTypeResource/create') {
      axios.get('/backoffice/filters/get_filters/?target=user').then(({ data }) => {
        setFilters(data);
      });
    }
  }, []);

  // 处理URL参数，填充title和program字段
  useEffect(() => {
    if (location && location.search && dsl) {
      const queryParams = queryString.parse(location.search);

      // 如果URL中有title参数，填充到表单中
      if (queryParams.title) {
        dispatch(change('commonDynamicForm', 'basic.title', queryParams.title));
      }

      // 如果URL中有program参数，填充到表单中
      if (queryParams.program) {
        // 直接使用URL参数中的内部值，不转换为显示文本
        dispatch(change('commonDynamicForm', 'basic.program.type', queryParams.program));
      }
    }
  }, [location, dispatch, dsl]);

  // 添加第二个useEffect来确保在表单完全加载后再次尝试填充
  useEffect(() => {
    if (location && location.search && dsl) {
      const queryParams = queryString.parse(location.search);

      // 使用更长的延迟确保表单完全渲染
      const timer = setTimeout(() => {
        if (queryParams.title) {
          dispatch(change('commonDynamicForm', 'basic.title', queryParams.title));
        }
        if (queryParams.program) {
          // 直接使用URL参数中的内部值，不转换为显示文本
          dispatch(change('commonDynamicForm', 'basic.program.type', queryParams.program));
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [location, dispatch, dsl]);
  /* eslint-disable */
  const handleFormSubmit = handleSubmit(async data => {
    const payload = data;

    // 填充缺失的 boolean 字段默认值（必须在其他处理之前）
    if (dsl) {
      fillBooleanDefaults(payload, dsl);
    }

    // 清理 fix 类型下的结构：确保 value 仅保留 item 与 value（内部自带 condition / reward_amount 等）
    if (payload.regular_account) {
      // 处理 group_exceptions
      if (Array.isArray(payload.regular_account.group_exceptions)) {
        payload.regular_account.group_exceptions.forEach(exception => {
          if (exception && exception.value) {
            if (exception.type === 'fix') {
              // 确保只保留 item 和 value 字段
              const { item, value } = exception.value;
              exception.value = {
                item: item || [],
                value: value || {},
              };
            } else if (exception.type === 'percentage' || exception.type === 'spread') {
              // 确保只保留 item 和 reward_amount 字段
              const { item, reward_amount } = exception.value;
              exception.value = {
                item: item || [],
                reward_amount: reward_amount || [],
              };
            }
          }
        });
      }

      // 处理 symbol_exceptions
      if (Array.isArray(payload.regular_account.symbol_exceptions)) {
        payload.regular_account.symbol_exceptions.forEach(exception => {
          if (exception && exception.data && exception.data.value) {
            if (exception.data.type === 'fix') {
              // 确保只保留 item 和 value 字段
              const { item, value } = exception.data.value;
              exception.data.value = {
                item: item || [],
                value: value || {},
              };
            } else if (exception.data.type === 'percentage' || exception.data.type === 'spread') {
              // 确保只保留 item 和 reward_amount 字段
              const { item, reward_amount } = exception.data.value;
              exception.data.value = {
                item: item || [],
                reward_amount: reward_amount || [],
              };
            }
          }
        });
      }
    }

    //  We need to populate empty TinyMCE fields with default value
    try {
      // 检查 tinymce 是否已加载（可能在 window 对象上）
      const tinymceInstance = window.tinymce || (typeof tinymce !== 'undefined' ? tinymce : null);
      if (tinymceInstance && tinymceInstance.editors) {
        const emptyTinyMCEFields = tinymceInstance.editors.filter(editor => editor.getContent() === '');
        emptyTinyMCEFields.forEach(field => fillEmptyField(payload, field.id, ''));
      }
    } catch (err) {
      console.log('err', err);
    }

    // Basically we check if null field exists and change his value
    const hiddenInputs = document.querySelectorAll('input[type="hidden"]');
    hiddenInputs.forEach(input => {
      if (input.id.includes('null')) {
        const name = input.id.substr(0, input.id.lastIndexOf('.')); // one.two.three -> one.two
        fillEmptyField(payload, name, { null: '' });
      }
    });

    // 处理隐藏的 regular_account 字段：如果 program.type 不是 traded_volume，则移除 regular_account
    // 检查 regular_account 是否被隐藏（通过检查 DOM 或 program 类型）
    const programType = payload?.basic?.program?.type || payload?.basic?.program;
    const isTradedVolume = programType === 'traded_volume';
    const isPnl = programType === 'pnl';
    // 如果 program 不是 traded_volume，且 regular_account 存在，检查它是否为空或应该被移除
    if (!isTradedVolume && payload.regular_account) {
      // 检查 regular_account 是否被隐藏（通过 DOM 检查）
      const regularAccountField = document.querySelector('[data-field-name="regular_account"]');
      const isHidden = regularAccountField && regularAccountField.classList.contains('ObjectField__hidden');
      // 如果字段被隐藏，或者 regular_account 是空对象或只包含空数组，则移除它
      if (isHidden) {
        delete payload.regular_account;
      } else {
        // 检查 regular_account 是否为空对象或只包含空数组
        const hasGroupExceptions =
          payload.regular_account.group_exceptions && payload.regular_account.group_exceptions.length > 0;
        const hasSymbolExceptions =
          payload.regular_account.symbol_exceptions && payload.regular_account.symbol_exceptions.length > 0;

        if (!hasGroupExceptions && !hasSymbolExceptions) {
          // 如果 regular_account 只包含空数组，且 program 不是 traded_volume，则移除它
          delete payload.regular_account;
        }
      }
    }
    // 处理隐藏的 pnl_reward 字段：如果 program.type 不是 pnl，则移除 pnl_reward
    if (!isPnl && payload.pnl_reward) {
      // 检查 pnl_reward 是否被隐藏（通过 DOM 检查）
      const pnlRewardField = document.querySelector('[data-field-name="pnl_reward"]');
      const isHidden = pnlRewardField && pnlRewardField.style.display === 'none';
      // 如果字段被隐藏，或者 pnl_reward 是空数组，则移除它
      if (isHidden) {
        delete payload.pnl_reward;
      } else {
        // 检查 pnl_reward 是否为空数组
        if (!payload.pnl_reward || (Array.isArray(payload.pnl_reward) && payload.pnl_reward.length === 0)) {
          delete payload.pnl_reward;
        }
      }
    }
    // 处理隐藏的 net_deposit_reward 字段：如果 program.type 不是 net_deposit，则移除 net_deposit_reward
    const isNetDeposit = programType === 'net_deposit';
    if (!isNetDeposit && payload.net_deposit_reward) {
      // 检查 net_deposit_reward 是否被隐藏（通过 DOM 检查）
      const netDepositRewardField = document.querySelector('[data-field-name="net_deposit_reward"]');
      const isHidden = netDepositRewardField && netDepositRewardField.style.display === 'none';
      // 如果字段被隐藏，或者 net_deposit_reward 是空数组，则移除它
      if (isHidden) {
        delete payload.net_deposit_reward;
      } else {
        // 检查 net_deposit_reward 是否为空数组
        if (
          !payload.net_deposit_reward ||
          (Array.isArray(payload.net_deposit_reward) && payload.net_deposit_reward.length === 0)
        ) {
          delete payload.net_deposit_reward;
        }
      }
    }

    if (document.querySelector('[data-field-name="recipient"]')) {
      const recipientCheckboxes = document.querySelectorAll('[name^="recipient."]');
      const recipientIds = Array.from(recipientCheckboxes).map(checkbox => {
        const name = checkbox.name;
        return name.split('.')[1];
      });

      // 确保 payload.recipient 是一个对象
      if (!payload.recipient) {
        payload.recipient = {};
      }

      // 确保所有 recipient ID 都存在于 payload.recipient 中，未勾选的设置为 false
      recipientIds.forEach(userId => {
        if (payload.recipient[userId] === undefined) {
          payload.recipient[userId] = false;
        }
      });
    }

    /* eslint-enable */
    function removeEmptyStringKeys(obj) {
      Object.keys(obj).forEach(key => {
        if (key === '') {
          delete obj[key];
        }
        // 特殊处理recipient字段，保持userId为key、布尔值为value的对象格式
        if (key === 'recipient' && obj[key]) {
          // 保留原始格式：{userId: true, ...}
          // 不需要额外处理，因为从checkbox获取的数据已经是这种格式
          // 只需确保userId为字符串类型
          const formattedRecipients = {};
          Object.keys(obj[key]).forEach(userId => {
            // 确保userId为字符串类型，值为布尔值
            formattedRecipients[userId.toString()] = obj[key][userId] === true;
          });
          obj[key] = formattedRecipients;
        }
      });
      return obj;
    }
    const intercepted = await runSubmitInterceptors(removeEmptyStringKeys(payload), initialValues);
    if (intercepted === null) {
      return undefined;
    }
    return submitFunction(intercepted, dispatch);
  }, dispatch);

  return (
    <div className="DynamicForm">
      <form onSubmit={handleFormSubmit}>
        <ObjectSchemaComposer schema={dsl} formName="commonDynamicForm" submitting={submitting} />
        {filters &&
          (window.location.pathname === '/admin/PlatformsCategory/PlatformAccountTypeResource/create' ||
            regexForAccoutTypeEdit.test(window.location.pathname)) && (
            <div className="CreatePaymentSystemForm__query" style={{ marginBottom: '40px' }}>
              <Field
                component={QueryBuilder}
                name="filterQuery"
                textId="justFilterConditions"
                disabled={submitting}
                filters={filters}
                initialQuery={initQueryFilterOnUpdateAccountType}
                onChangeQuery={() => {}}
              />
              <style jsx global>
                {`
                  :global(.ruleGroup) {
                    margin-top: 0 !important;
                  }
                `}
              </style>
            </div>
          )}
        <div className="GenericForm__button-inner">
          <DefaultButton
            textId="doSave"
            type="submit"
            filled
            disabled={invalid || submitting || disabled || shouldDisableForRewardTable || (isTagForm && pristine)}
            loading={submitting}
          />
          {isTagForm && <DefaultButton textId="justCancel" type="button" onClick={() => history.goBack()} />}
        </div>
        {isTagForm && (
          <style jsx>
            {`
              .GenericForm__button-inner {
                display: flex;
                align-items: center;
                gap: 10px;
                margin-top: 20px;
              }
            `}
          </style>
        )}
      </form>
      <BasicModal isOpen={isModalOpen} captionId="justError" description={formError} onRequestClose={toggleModal} />
    </div>
  );
};

DynamicForm.propTypes = {
  dsl: PropTypes.object.isRequired,
  submitFunction: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
  userFilters: PropTypes.object,
  formError: PropTypes.string,
  submitFailed: PropTypes.bool,
  disabled: PropTypes.bool,
  submitting: PropTypes.bool,
  location: PropTypes.object,
  pristine: PropTypes.bool,
  history: PropTypes.object.isRequired,
  isTagForm: PropTypes.bool,
};

DynamicForm.defaultProps = {
  userFilters: null,
  formError: '',
  submitFailed: false,
  disabled: false,
  submitting: false,
  location: null,
  pristine: true,
  isTagForm: false,
};

export default compose(
  withRouter,
  FormMessageHOC,
  connect(state => {
    const selector = formValueSelector('commonDynamicForm');
    return {
      formError: state.admin.dynamics.error,
      program: selector(state, 'basic.program.type'),
    };
  }),
  reduxForm({
    form: 'commonDynamicForm',
    enableReinitialize: true,
    onSubmitFail(result, dispatch, error, props) {
      props.showFormFailMessage(props.failMessageTextId);
    },
    onSubmitSuccess(result, dispatch, props) {
      props.showFormSuccessMessage(props.successMessageTextId);
      if (props.afterSubmitSuccess) {
        props.afterSubmitSuccess();
      }
    },
  })
)(DynamicForm);
export { DynamicForm };
